import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "entwicklung", label: "Entwicklung" },
  { key: "strategie", label: "Strategieanalyse" },
  { key: "risiko", label: "Risikoanalyse" },
  { key: "markt", label: "Marktanalyse" },
  { key: "verhalten", label: "Trading-Verhalten" },
  { key: "ki", label: "KI-Erkenntnisse" },
];

const tick = { fontSize: 12, fill: "hsl(220 9% 62%)" };
const tooltipStyle = {
  border: "1px solid hsl(217 25% 22%)", borderRadius: 12,
  background: "hsl(217 33% 17%)", color: "hsl(220 13% 91%)",
  fontFamily: "Inter", fontSize: 12,
};
// Farbrampe für Pie/Bar
const PALETTE = ["#4ade80", "#38bdf8", "#f59e0b", "#f472b6", "#a78bfa", "#22d3ee", "#fb7185"];

export default function Analytics() {
  const [tab, setTab] = useState("entwicklung");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await axios.get(`${API}/api/trades/analytics`, { withCredentials: true });
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Analyse konnte nicht geladen werden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Analyse"
        title="Analyse"
        description="Sechs Blickwinkel auf dein Handeln. Wechsle zwischen den Reitern."
        testid="analytics-header"
      />

      {loading ? (
        <div className="mt-6 grid place-items-center h-64 rounded-xl border border-dashed border-border" data-testid="analytics-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : error ? (
        <div className="mt-6 rounded-xl border border-[hsl(0_66%_50%/0.35)] bg-[hsl(0_66%_50%/0.05)] p-4 text-sm text-[hsl(0_66%_72%)]" data-testid="analytics-error">
          {error}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} data-testid="analytics-tabs">
          <TabsList className="rounded-full h-11 p-1 bg-secondary flex-wrap">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key}
                className="rounded-full px-4 h-9 text-sm data-[state=active]:bg-background"
                data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="entwicklung" className="mt-6">
            <ChartCard title="Kumulierte P/L-Entwicklung"
              subtitle="Netto-P/L (Gebühren bereits abgezogen), aufaddiert über alle abgeschlossenen Trades.">
              {data.equity?.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.equity} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(217 25% 22%)" />
                    <XAxis dataKey="at" tick={tick} tickFormatter={(v) => new Date(v).toLocaleDateString("de-DE", { month: "short", day: "2-digit" })} />
                    <YAxis tick={tick} tickFormatter={(v) => v.toLocaleString("de-DE", { maximumFractionDigits: 2 })} />
                    <Tooltip contentStyle={tooltipStyle}
                      labelFormatter={(v) => new Date(v).toLocaleString("de-DE")}
                      formatter={(v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                    <Line type="monotone" dataKey="cum_pl" stroke="#4ade80" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyChart hint="Erfasse deine ersten Trades — die Kurve entsteht ab dem ersten geschlossenen Trade." />}
            </ChartCard>
          </TabsContent>

          <TabsContent value="strategie" className="mt-6">
            <ChartCard title="Strategien im Vergleich"
              subtitle="Netto-P/L und Trefferquote pro Strategie.">
              {data.strategies?.length ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data.strategies} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(217 25% 22%)" />
                      <XAxis dataKey="strategy" tick={tick} />
                      <YAxis tick={tick} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="pl" name="Netto P/L">
                        {data.strategies.map((s, i) => (
                          <Cell key={i} fill={s.pl >= 0 ? "#4ade80" : "#f87171"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="analytics-strat-list">
                    {data.strategies.map((s) => (
                      <li key={s.strategy} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.strategy}</div>
                          <div className="text-muted-foreground text-[11px]">{s.trades} Trades{s.win_rate != null ? ` · ${s.win_rate}\u00A0% Trefferquote` : ""}</div>
                        </div>
                        <div className={`font-mono text-sm ${s.pl >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>
                          {s.pl >= 0 ? "+" : "−"}{Math.abs(s.pl).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : <EmptyChart hint="Weise deinen Trades Strategien zu, um Vergleiche zu sehen." />}
            </ChartCard>
          </TabsContent>

          <TabsContent value="risiko" className="mt-6">
            <ChartCard title="SL-Abstand — Verteilung"
              subtitle="Prozentualer Kurs-Abstand vom Entry bis zum Stop-Loss, pro Trade.">
              {data.risk?.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.risk} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(217 25% 22%)" />
                    <XAxis dataKey="bucket" tick={tick} />
                    <YAxis tick={tick} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="trades" fill="#38bdf8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart hint="Trage SL-Preise ein, um die Risiko-Verteilung zu sehen." />}
            </ChartCard>
          </TabsContent>

          <TabsContent value="markt" className="mt-6">
            <ChartCard title="Verteilung nach Markt"
              subtitle="Wie sich deine Trades über die Marktsegmente verteilen.">
              {data.markets?.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Pie data={data.markets} dataKey="trades" nameKey="market" outerRadius={100} label>
                      {data.markets.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyChart hint="Zeigt sich, sobald Trades in verschiedenen Segmenten erfasst wurden." />}
            </ChartCard>
          </TabsContent>

          <TabsContent value="verhalten" className="mt-6">
            <ChartCard title="Aktivität nach Uhrzeit (UTC)"
              subtitle="Wann du in Trades einsteigst — Stunden mit den meisten Öffnungen.">
              {data.behavior?.some((b) => b.trades > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.behavior} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(217 25% 22%)" />
                    <XAxis dataKey="hour" tick={tick} />
                    <YAxis tick={tick} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="trades" fill="#a78bfa" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart hint="Muster nach Uhrzeit werden sichtbar, sobald mehrere Trades mit Zeitstempel vorliegen." />}
            </ChartCard>
          </TabsContent>

          <TabsContent value="ki" className="mt-6">
            {data.total_trades >= 10 ? (
              <ChartCard title="KI-Erkenntnisse"
                subtitle="Erste Muster aus deinen Trades — der KI-Coach hebt hier später konkrete Beobachtungen hervor.">
                <div className="text-sm text-muted-foreground">
                  {data.total_trades} Trades erfasst. Frag' den Coach gezielt zu einem Trade („Mit KI-Coach besprechen") — er zieht dabei die passenden Muster.
                </div>
              </ChartCard>
            ) : (
              <div className="card-surface p-10 text-center" data-testid="ki-insights-empty">
                <div className="font-heading text-lg">Noch keine KI-Erkenntnisse</div>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Der Coach zieht Muster aus deinen Trades. Sobald du mindestens 10 Einträge hast, erscheinen hier objektive Beobachtungen. Aktuell: {data.total_trades}.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="card-surface p-6">
      <h2 className="font-heading text-xl">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function EmptyChart({ hint }) {
  return (
    <div className="h-64 grid place-items-center rounded-lg border border-dashed border-border">
      <div className="text-center px-6">
        <div className="text-sm text-muted-foreground">Noch keine Daten für diese Auswertung.</div>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </div>
    </div>
  );
}
